import { Component, Input, Output, EventEmitter, ElementRef, HostListener, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SortColumn, SortConfig } from './custom-sort-panel.model';

type DropdownOption = { id: string; text: string };

@Component({
    selector: 'app-sort-panel',
    templateUrl: './custom-sort-panel.component.html',
    styleUrls: ['./custom-sort-panel.component.scss'],
    standalone: false
})
export class CustomSortPanelComponent implements AfterViewInit, OnChanges {
  @Input() columns: SortColumn[] = [];
  @Input() currentSort: SortConfig | null = null;

  @Output() sortChange = new EventEmitter<SortConfig | null>();
  @Output() panelClose = new EventEmitter<void>();

  @ViewChild('panel') panelRef!: ElementRef<HTMLDivElement>;
  @ViewChild('sortButton') sortButtonRef!: ElementRef<HTMLButtonElement>;

  isOpen = false;

  form: FormGroup;

  sortKeyOptions: DropdownOption[] = [];
  directionOptions: DropdownOption[] = [
    { id: 'ASC', text: 'SORTS_PANEL.DIRECTION_ASC' },
    { id: 'DESC', text: 'SORTS_PANEL.DIRECTION_DESC' }
  ];

  constructor(
    private elementRef: ElementRef,
    private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({
      sortKey: [[], [Validators.required]],
      sortDirection: [[], [Validators.required]]
    });
  }

  ngAfterViewInit(): void {
    this.syncOptionsFromColumns();
    this.syncFormFromInputs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const columnsChanged = !!changes['columns'];
    const currentSortChanged = !!changes['currentSort'];

    if (columnsChanged) {
      this.syncOptionsFromColumns();
    }

    if (columnsChanged || currentSortChanged) {
      this.syncFormFromInputs();
    }
  }

  togglePanel(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    this.isOpen = !this.isOpen;

    if (!this.isOpen) {
      return;
    }

    this.syncFormFromInputs();

    setTimeout(() => {
      const panelElement = this.panelRef?.nativeElement;
      if (!panelElement) {
        return;
      }

      const firstFocusable = panelElement.querySelector<HTMLElement>('.cd-control, button');
      firstFocusable?.focus();
    });
  }

  public onPanelContentClick(event: MouseEvent): void {
    if (!event) {
      return;
    }

    event.stopPropagation();
  }

  private syncOptionsFromColumns(): void {
    const safeColumns = Array.isArray(this.columns) ? this.columns : [];

    const nextOptions: DropdownOption[] = [];

    for (const col of safeColumns) {
      if (!col) {
        continue;
      }

      const key = String(col.key ?? '').trim();
      const label = String(col.label ?? '').trim();

      if (key.length === 0) {
        continue;
      }

      nextOptions.push({
        id: key,
        text: label
      });
    }

    this.sortKeyOptions = nextOptions;
  }

  private syncFormFromInputs(): void {
    const hasCurrentSort = this.currentSort && String(this.currentSort.key ?? '').trim().length > 0;

    if (hasCurrentSort) {
      this.form.patchValue({
        sortKey: [this.currentSort!.key],
        sortDirection: [this.currentSort!.direction]
      });
      return;
    }

    // empty to display placeholder
    this.form.patchValue({
      sortKey: [],
      sortDirection: []
    });
  }

  closePanel(): void {
    this.isOpen = false;
    this.panelClose.emit();

    setTimeout(() => {
      this.sortButtonRef.nativeElement.focus();
    });
  }

  apply(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const sortKeyArray = this.form.get('sortKey')?.value;
    const directionArray = this.form.get('sortDirection')?.value;

    const selectedKey = Array.isArray(sortKeyArray) && sortKeyArray.length > 0 ? sortKeyArray[0] : '';
    const selectedDirection = Array.isArray(directionArray) && directionArray.length > 0 ? directionArray[0] : '';

    if (!selectedKey || !selectedDirection) {
      return;
    }

    this.sortChange.emit({
      key: selectedKey,
      direction: selectedDirection
    });

    this.closePanel();
  }

  clear(): void {
    this.sortChange.emit(null);
    this.closePanel();
  }

  cancel(): void {
    this.closePanel();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }

    const targetNode = event.target as Node | null;
    if (!targetNode) {
      return;
    }

    const clickedInside = this.elementRef.nativeElement.contains(targetNode);
    if (!clickedInside) {
      this.closePanel();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.closePanel();
    }
  }
}
